import { Award, CalendarCheck, Clock, Flame, PhoneOff, ShieldCheck, TrendingDown, TrendingUp, Minus, UserX } from 'lucide-react'
import type { PerformanceSummary } from '../types.js'
import type { WorkerTier } from '../../../modules/core/index.js'
import { TIER_HEX, TIER_LABELS } from '../../../modules/core/index.js'
import { SummaryScoreChart } from './SummaryScoreChart.js'

const TIER_RANK: Record<WorkerTier, number> = { gold: 3, silver: 2, bronze: 1, probationary: 0 }

function percent(numerator: number, denominator: number): number | null {
  return denominator === 0 ? null : Math.round((numerator / denominator) * 1000) / 10
}

/** The one sentence a reader takes away: where the tier went, and the fact that most explains it. */
export function headlineOf(summary: PerformanceSummary): string {
  const { tierAtStart, tierAtEnd, shifts, provenance } = summary
  if (provenance.closedMonths === 0) {
    return 'No month of this span has been scored yet.'
  }
  const onTime = percent(shifts.onTime, shifts.completed)
  const movement =
    tierAtStart && tierAtEnd && TIER_RANK[tierAtEnd] > TIER_RANK[tierAtStart]
      ? `Up from ${TIER_LABELS[tierAtStart]} to ${TIER_LABELS[tierAtEnd]}`
      : tierAtStart && tierAtEnd && TIER_RANK[tierAtEnd] < TIER_RANK[tierAtStart]
        ? `Down from ${TIER_LABELS[tierAtStart]} to ${TIER_LABELS[tierAtEnd]}`
        : tierAtStart && tierAtEnd
          ? `${TIER_LABELS[tierAtEnd]} throughout`
          : tierAtEnd
            ? `Reached ${TIER_LABELS[tierAtEnd]}`
            : 'No tier set'
  const facts = [onTime === null ? null : `${onTime}% on time`, `${shifts.callOuts} ${shifts.callOuts === 1 ? 'call-out' : 'call-outs'}`, shifts.noShows > 0 ? `${shifts.noShows} ${shifts.noShows === 1 ? 'no-show' : 'no-shows'}` : null].filter((fact): fact is string => fact !== null)
  return `${movement} over ${provenance.closedMonths} closed ${provenance.closedMonths === 1 ? 'month' : 'months'}: ${facts.join(', ')}.`
}

function TierChip({ tier }: { tier: WorkerTier | null }) {
  if (!tier) {
    return <span className="text-sm font-bold text-ink-mid">—</span>
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-black uppercase tracking-wide text-white" style={{ backgroundColor: TIER_HEX[tier] }}>
      <Award aria-hidden="true" className="h-3.5 w-3.5" />
      {TIER_LABELS[tier]}
    </span>
  )
}

interface StatProps {
  icon: React.ReactNode
  value: string
  label: string
  sub?: string
  tone?: 'good' | 'warn' | 'plain'
}

function Stat({ icon, value, label, sub, tone = 'plain' }: StatProps) {
  const toneClass = tone === 'good' ? 'text-green' : tone === 'warn' ? 'text-coral' : 'text-ink'
  return (
    <div className="flex items-start gap-3 rounded-lg border border-marine/15 bg-white px-3 py-2.5">
      <span className={`mt-0.5 shrink-0 ${toneClass}`}>{icon}</span>
      <div className="min-w-0">
        <div className={`text-xl font-black leading-tight ${toneClass}`}>{value}</div>
        <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{label}</div>
        {sub && <div className="text-[10px] text-ink-mid">{sub}</div>}
      </div>
    </div>
  )
}

/**
 * A summary in bold strokes, for anyone who needs the shape of a worker's
 * span before the detail: the tier's journey, a headline sentence, the
 * score by month, and the counts that matter as big numbers with an icon
 * each. Shared by the printed document, the roster's Performance tab and
 * the worker's own app, so every reader sees the same picture.
 */
export function PerformanceHighlights({ summary, compact = false }: { summary: PerformanceSummary; compact?: boolean }) {
  const { shifts, consistency, barriers, provenance } = summary
  // Final scores only: a month still running or never closed has no final to average.
  const scored = summary.months.filter((month) => month.state === 'final' && month.score !== null)
  const average = scored.length === 0 ? null : Math.round((scored.reduce((total, month) => total + (month.score ?? 0), 0) / scored.length) * 10) / 10
  const hasClosedMonths = provenance.closedMonths > 0
  const onTime = percent(shifts.onTime, shifts.completed)
  const movedUp = summary.tierAtStart && summary.tierAtEnd && TIER_RANK[summary.tierAtEnd] > TIER_RANK[summary.tierAtStart]
  const movedDown = summary.tierAtStart && summary.tierAtEnd && TIER_RANK[summary.tierAtEnd] < TIER_RANK[summary.tierAtStart]
  const Movement = movedUp ? TrendingUp : movedDown ? TrendingDown : Minus

  return (
    <div className="rounded-lg border border-marine/20 bg-canvas p-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-2">
          <TierChip tier={summary.tierAtStart} />
          <Movement aria-hidden="true" className={`h-5 w-5 ${movedUp ? 'text-green' : movedDown ? 'text-coral' : 'text-ink-mid'}`} />
          <TierChip tier={summary.tierAtEnd} />
        </div>
        {average !== null && (
          <div className="flex items-baseline gap-1.5">
            <span className="text-3xl font-black text-marine-dk">{average}</span>
            <span className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">avg final score</span>
          </div>
        )}
      </div>
      <p className="mt-2 text-sm font-bold text-ink">{headlineOf(summary)}</p>

      {!compact && hasClosedMonths && (
        <div className="mt-3">
          <SummaryScoreChart months={summary.months} />
        </div>
      )}

      {hasClosedMonths && (
      <div className={`mt-3 grid gap-2 ${compact ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'}`}>
        <Stat icon={<Clock className="h-5 w-5" />} value={onTime === null ? '—' : `${onTime}%`} label="On time" sub={`${shifts.onTime} of ${shifts.completed} worked`} tone={onTime === null ? 'plain' : onTime >= 90 ? 'good' : onTime < 75 ? 'warn' : 'plain'} />
        <Stat icon={<PhoneOff className="h-5 w-5" />} value={String(shifts.callOuts)} label="Call-outs" sub={shifts.shortNoticeCallOuts > 0 ? `${shifts.shortNoticeCallOuts} short notice` : `of ${shifts.scheduled} scheduled`} tone={shifts.callOuts === 0 ? 'good' : shifts.shortNoticeCallOuts > 0 ? 'warn' : 'plain'} />
        <Stat icon={<UserX className="h-5 w-5" />} value={String(shifts.noShows)} label="No-shows" tone={shifts.noShows === 0 ? 'good' : 'warn'} />
        <Stat icon={<Flame className="h-5 w-5" />} value={String(consistency.longestOnTimeRun)} label="Longest on-time run" sub="shifts in a row" />
        <Stat icon={<ShieldCheck className="h-5 w-5" />} value={`${consistency.longestWithoutCallOutDays}d`} label="Without a call-out" sub="longest stretch" />
        <Stat icon={<CalendarCheck className="h-5 w-5" />} value={`${provenance.closedMonths}/${summary.months.length}`} label="Months closed" sub={barriers.raised > 0 ? `${barriers.raised} ${barriers.raised === 1 ? 'note' : 'notes'} from the worker` : undefined} />
      </div>
      )}
    </div>
  )
}
